import { useState } from 'react';

interface UseFileUploadProps {
  acceptedTypes?: string[];
  maxFileSize?: number; // in bytes
}

interface UseFileUploadReturn {
  file: File | null;
  setFile: (file: File | null) => void;
  isDragOver: boolean;
  uploading: boolean;
  setUploading: (uploading: boolean) => void;
  error: string | null;
  handleDragOver: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: () => void;
}

export const useFileUpload = ({
  acceptedTypes = ['image/*', '.pdf'],
  maxFileSize = 10 * 1024 * 1024, // 10MB default
}: UseFileUploadProps = {}): UseFileUploadReturn => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): boolean => {
    setError(null);

    // Check file size
    if (file.size > maxFileSize) {
      setError(`File size too large. Maximum size is ${maxFileSize / 1024 / 1024}MB`);
      return false;
    }

    // Check file type
    const isAcceptedType = acceptedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type);
      }
      return file.type.match(type.replace('*', '.*'));
    });

    if (!isAcceptedType) {
      setError('Invalid file type. Please upload an image or PDF file.');
      return false;
    }

    return true;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setError(null);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      const droppedFile = droppedFiles[0];

      if (validateFile(droppedFile)) {
        setFile(droppedFile);
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (selectedFile) {
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    setError(null);
  };

  return {
    file,
    setFile,
    isDragOver,
    uploading,
    setUploading,
    error,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileInputChange,
    removeFile,
  };
};